export const ESSNVerificationStatus = {
  approved: "approved",

  // 1st verification failed, user will provide more info
  id_verification_required: "id_verification_required",

  // Intermediate state where internal team reviews id verification submission after failure
  id_verification_internal_review_pending:
    "id_verification_internal_review_pending",

  // 2nd verification failed, user will provide more info
  social_security_card_required: "social_security_card_required",

  // Intermediate state where internal team reviews social security card submission after failure
  social_security_card_internal_review_pending:
    "social_security_card_internal_review_pending",

  // hard decline, final decision
  declined: "declined",

  // override statuses applied by Zeal team
  compliance_approved: "compliance_approved",
  compliance_declined: "compliance_declined",
} as const;

export type TSSNVerificationStatus =
  (typeof ESSNVerificationStatus)[keyof typeof ESSNVerificationStatus];

export const positiveSSNVerificationStatuses = [
  ESSNVerificationStatus.approved,
  ESSNVerificationStatus.compliance_approved,
] as const;

export const intermediateNegativeSSNVerificationStatuses = [
  ESSNVerificationStatus.id_verification_required,
  ESSNVerificationStatus.social_security_card_required,
  ESSNVerificationStatus.id_verification_internal_review_pending,
  ESSNVerificationStatus.social_security_card_internal_review_pending,
] as const;

export const statusesRequiringWorkerAction = [
  ESSNVerificationStatus.id_verification_required,
  ESSNVerificationStatus.social_security_card_required,
] as const;

// SSN verification failure after these statuses requires internal review
export const precursorStatusesForInternalReview = [
  ESSNVerificationStatus.id_verification_required,
  ESSNVerificationStatus.social_security_card_required,
] as const;

export const hardNegativeSSNVerificationStatuses = [
  ESSNVerificationStatus.declined,
  ESSNVerificationStatus.compliance_declined,
] as const;

type TSSNPositiveStatus = (typeof positiveSSNVerificationStatuses)[number];
export const isPositiveSSNVerificationStatus = (
  status: TSSNVerificationStatus
): status is TSSNPositiveStatus =>
  (positiveSSNVerificationStatuses as readonly string[]).includes(status);

export const isHardNegativeSSNVerificationStatus = (
  status: TSSNVerificationStatus
): boolean =>
  (hardNegativeSSNVerificationStatuses as readonly string[]).includes(status);

export const isIntermediateNegativeSSNVerificationStatus = (
  status: TSSNVerificationStatus
): boolean =>
  (intermediateNegativeSSNVerificationStatuses as readonly string[]).includes(
    status
  );

export const isSSNVerificationStatusPrecursorToInternalReview = (
  status: TSSNVerificationStatus
): boolean =>
  (precursorStatusesForInternalReview as readonly string[]).includes(status);

type TSSNActionStatus = (typeof statusesRequiringWorkerAction)[number];
export const doesSSNVerificationStatusRequireWorkerAction = (
  status: TSSNVerificationStatus
): status is TSSNActionStatus =>
  (statusesRequiringWorkerAction as readonly string[]).includes(status);
